import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Trophy, Crown, Flag, Star, Crosshair, Mountains, Lightning, ShieldCheck,
  Circle, ListBullets, MapTrifold, House, Target, Bird,
} from "@phosphor-icons/react";

/**
 * /badges — Achievement gallery.
 *
 * Renders every badge from /api/badges/me with an earned/locked state.
 * Earned ones are coloured + show the date earned; locked ones are dimmed
 * with the description as the unlock hint. Filterable by category.
 */

// Map backend icon-name strings to actual Phosphor components. Keeping the
// mapping in the frontend lets the backend stay icon-library-agnostic.
const ICONS = {
  Trophy, Crown, Flag, Star, Crosshair, Mountains, Lightning, ShieldCheck,
  Circle, ListBullets, MapTrifold, House, Target, Bird,
};

const CATEGORY_LABEL = {
  scoring: "Scoring",
  streak: "Streaks & Quality",
  volume: "Volume",
  course: "Course Exploration",
};

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return null;
  }
}

function BadgeTile({ b }) {
  const Icon = ICONS[b.icon] || Trophy;
  const earned = b.earned;
  return (
    <div
      data-testid={`badge-tile-${b.id}`}
      className={`card-default p-4 flex flex-col items-center text-center transition ${
        earned ? "border-[#E05D3A]/30" : "opacity-55 hover:opacity-80"
      }`}
    >
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-2 ${
        earned ? "bg-[#E05D3A] text-white" : "bg-[#0F2537]/5 text-[#0F2537]/40"
      }`}>
        <Icon weight={earned ? "fill" : "regular"} size={26} />
      </div>
      <div className={`font-heading font-bold text-sm leading-tight ${earned ? "text-[#0F2537]" : "text-[#0F2537]/70"}`}>
        {b.name}
      </div>
      <div className="text-[10px] text-[#0F2537]/55 mt-1.5 leading-snug min-h-[26px]">
        {b.description}
      </div>
      {earned ? (
        <div className="mt-2 text-[10px] font-bold text-[#1E5631]" data-testid={`badge-earned-${b.id}`}>
          Earned · {formatDate(b.earned_at)}
        </div>
      ) : (
        <div className="mt-2 text-[10px] font-bold text-[#0F2537]/35 uppercase tracking-wider">Locked</div>
      )}
    </div>
  );
}

export default function Badges() {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/badges/me")
      .then((r) => setBadges(r.data))
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    const earned = badges.filter((b) => b.earned).length;
    return { earned, total: badges.length };
  }, [badges]);

  const categories = useMemo(() => {
    const set = new Set(badges.map((b) => b.category));
    return ["all", ...Array.from(set)];
  }, [badges]);

  const filtered = filter === "all" ? badges : badges.filter((b) => b.category === filter);

  return (
    <div className="space-y-6" data-testid="badges-page">
      <div className="rise">
        <span className="label-eyebrow">Achievements</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">
          Your trophy cabinet.
        </h1>
        <p className="text-[#0F2537]/70 mt-2 max-w-xl">
          Every birdie, every break-90, every Fiji course conquered — Bula Golf
          remembers the moments. Keep playing to unlock the rest.
        </p>
      </div>

      <div className="card-default p-5 flex flex-wrap items-center gap-5 justify-between">
        <div>
          <span className="label-eyebrow">Progress</span>
          <div className="font-heading font-black text-3xl mt-0.5" data-testid="badges-progress">
            {stats.earned} <span className="text-[#0F2537]/40 text-xl font-bold">/ {stats.total}</span>
          </div>
        </div>
        <div className="flex-1 max-w-md">
          <div className="h-2 rounded-full bg-[#0F2537]/10 overflow-hidden">
            <div
              className="h-full bg-[#E05D3A] transition-all"
              style={{ width: `${stats.total ? (stats.earned / stats.total) * 100 : 0}%` }}
            />
          </div>
        </div>
        <Link
          to="/rounds/new"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#0F2537] text-white text-sm font-bold hover:bg-[#0F2537]/90 transition"
          data-testid="badges-cta-new-round"
        >
          <Flag size={14} weight="fill" /> Play a round
        </Link>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="badges-category-filter">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`badges-filter-${c}`}
            onClick={() => setFilter(c)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition border ${
              filter === c
                ? "bg-[#0F2537] text-white border-[#0F2537]"
                : "bg-white text-[#0F2537]/70 border-[#E2E0D8] hover:border-[#0F2537]"
            }`}
          >
            {c === "all" ? "All" : CATEGORY_LABEL[c] || c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center text-[#0F2537]/50">Loading badges…</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filtered.map((b) => <BadgeTile key={b.id} b={b} />)}
        </div>
      )}
    </div>
  );
}
